import React from "react";
import { css } from "styled-components";

export const drawDebug = (): string[] => [ "background: rgba(0, 255, 255, 0.1);", "border: 1px dashed rgba(255, 0, 255, 1);" ];

export type HedronProcessAlas = "half" | "quarter" | "third" | "twoThirds" | "threeQuarters";

/**
 * Returns % width based on alias. Returns
 * string with if alias is not passed
 * @param {string} alias
 */
const processAlias = (alias: HedronProcessAlas) => {
  switch (alias) {
    case "half":
      return "50%";
    case "quarter":
      return "25%";
    case "third":
      return "33.3333333%";
    case "twoThirds":
      return "66.666666%";
    case "threeQuarters":
      return "75%";
    default:
      return alias;
  }
};

/**
 * Returns an array of valid css declarations generated
 * from the react props supplied.
 *
 * @param {array} props list of react props
 */
export const generateStyles = (props: AnyProps): Array<string | undefined> => [
  props.padding && `padding: ${props.padding};`,
  props.margin && `margin: ${props.margin};`,
  props.width && `width: ${processAlias(props.width)};`,
  props.height && `height: ${props.height};`,
  props.visibility && `visibility: ${props.visibility};`,
  props.display && `display: ${props.display};`,
  props.opacity && `opacity: ${props.opacity};`,
  props.color && `color: ${props.color};`,
  props.background && `background: ${props.background};`,
  props.border && `border: ${props.border};`,
  props.fontSize && `font-size: ${props.fontSize};`,
  props.fontWeight && `font-weight: ${props.fontWeight};`,
  props.fontStyle && `font-style: ${props.fontStyle};`,
  props.fontFamily && `font-family: ${props.fontFamily};`,
  props.lineHeight && `line-height: ${props.lineHeight};`,
  props.textTransform && `text-transform: ${props.textTransform};`,
  props.hidden && "display: none;"
];

interface ParsedBreakpoint {
  lt?: string;
  gt?: string;
  range?: string[];
}

/**
 * This will parse a breakpoint string (i.e. -500) and return
 * the operation needed to generate a media query
 * @param {string} breakpoint
 */
const parseBreakpoint = (breakpoint: string): ParsedBreakpoint => {
  switch (breakpoint.charAt(0)) {
    // less than
    case "-":
      return { lt: breakpoint.substring(1) };
    // more than
    case "+":
      return { gt: breakpoint.substring(1) };
    // range
    default: {
      const range = breakpoint.split("-");

      if (range.length !== 2) {
        console.error(
          "Invalid breakpoint range. The proper syntax for a range is: min-max"
        );
      }

      return { range };
    }
  }
};

/**
 * Generates a media query from a breakpoint operation
 *
 * @param {Object} operation
 */
const media = (operation: ParsedBreakpoint) => {
  if (operation.lt) {
    return `@media (max-width: ${operation.lt}px)`;
  } else if (operation.gt) {
    return `@media (min-width: ${operation.gt}px)`;
  } else if (operation.range) {
    return `@media (min-width: ${operation.range[ 0 ]}px) and (max-width: ${
      operation.range[ 1 ]
    }px)`;
  }

  return "";
};

/**
 * Generates valid CSS media queries from a component's props
 *
 * @param {Object} props
 */
export const breakpoints = (props: AnyProps): Array<[string] | undefined> | null => {
  if (!props.breakpoints) return null;

  return Object.keys(props.breakpoints).map(
    id =>
      props[ id ] && [
        css`
          ${media(parseBreakpoint(props.breakpoints[ id ]))} {
            ${generateStyles(props[ id ])};
            ${flex(props[ id ])};
          }
        `
      ]
  );
};

const translations = {
  direction: {
    horizontal: "row",
    vertical: "column"
  },
  allowWrap: {
    true: "wrap",
    1: "wrap",
    false: "nowrap",
    0: "nowrap"
  },
  align: {
    top: "flex-start",
    left: "flex-start",
    bottom: "flex-end",
    right: "flex-end",
    center: "center",
    middle: "center"
  }
};

export const flex = ({
  flex,
  fill,
  direction,
  allowWrap,
  valign,
  halign,
  shiftLeft,
  shiftRight,
  shiftUp,
  shiftDown
}: AnyProps): string[] => {
  const props = [];

  if (flex) props.push(`flex: ${flex};`);

  if (fill) props.push("flex: 1 1 auto;");

  if (direction)
    props.push(`flex-direction: ${translations.direction[ direction ]};`);

  if (allowWrap) props.push(`flex-wrap: ${translations.allowWrap[ allowWrap ]};`);

  if (halign) {
    if (direction === "horizontal")
      props.push(`justify-content: ${translations.align[ halign ]};`);
    else props.push(`align-items: ${translations.align[ halign ]};`);
  }

  if (valign) {
    if (direction === "horizontal")
      props.push(`align-items: ${translations.align[ valign ]};`);
    else props.push(`justify-content: ${translations.align[ valign ]};`);
  }

  if (shiftLeft) props.push("margin-right: auto;");

  if (shiftRight) props.push("margin-left: auto;");

  if (shiftUp) props.push("margin-bottom: auto;");

  if (shiftDown) props.push("margin-top: auto;");

  return props;
};

export type AnyProps = Record<string, any>;
export type HTMLElementOrReactComponent = keyof JSX.IntrinsicElements | React.ComponentType<AnyProps>;

// Need to define a return type here, otherwise ...props is typed as any and the theme prop gets lost
export const stripBoolean = (Element: HTMLElementOrReactComponent): React.ReactFCWithChildren<AnyProps> => ({
  debug: _debug,
  fluid: _fluid,
  fill: _fill,
  allowWrap: _allowWrap,
  shiftRight: _shiftRight,
  shiftLeft: _shiftLeft,
  shiftUp: _shiftUp,
  shiftDown: _shiftDown,
  ...props
}) => <Element {...props} />;
